/**
 * Shared client-side media dimension measurement.
 *
 * Measures `width` and `height` from a File or URL and derives a canonical
 * `aspectRatio` string (e.g. "16:9") by snapping to the nearest entry in
 * CANONICAL_ASPECT_RATIOS. Always resolves — a failed measurement simply
 * returns an empty object so upload flows are never blocked.
 *
 * SSR-safe: returns empty object when `window` is unavailable.
 */

import type { MediaDimensionFields } from '../types'

export type { MediaDimensionFields }

export interface RawMediaDimensions {
  width?: number
  height?: number
}

export type MediaDimensionKind = 'image' | 'video'

/**
 * Canonical aspect ratios the app emits. Every computed aspectRatio snaps to
 * the nearest entry in this list so downstream consumers (Flutter layout,
 * responsive image buckets, etc.) only have to handle a bounded set.
 *
 * These are "W:H" string labels used on media payloads over the wire.
 * NOT the same as the numeric `AspectRatio` enum in `../types` — that enum
 * feeds the cropper UI with JS ratio numbers and has no wire semantics.
 *
 * Order is cosmetic; snapping uses numeric distance.
 */
export const CANONICAL_ASPECT_RATIOS = [
  '1:1',
  '16:9',
  '9:16',
  '4:3',
  '4:5',
  '3:2',
  '2:3',
  '21:9',
  '2:1',
] as const

export type CanonicalAspectRatio = (typeof CANONICAL_ASPECT_RATIOS)[number]

const CANONICAL_RATIO_VALUES: ReadonlyArray<{ label: CanonicalAspectRatio; value: number }> =
  CANONICAL_ASPECT_RATIOS.map(label => {
    const [w, h] = label.split(':').map(Number)
    return { label, value: w / h }
  })

/**
 * Maximum relative distance between the true ratio and the nearest canonical
 * ratio before we refuse to snap. Ratios beyond this tolerance (e.g. 1:4
 * vertical panoramas) return undefined instead of being forced into a
 * misleading canonical bucket.
 */
const SNAP_TOLERANCE = 0.2

/**
 * Snap a raw width/height to the nearest canonical aspect ratio.
 * Returns undefined when either input is missing, non-positive, or the
 * nearest canonical ratio is outside SNAP_TOLERANCE of the true ratio.
 */
export const computeAspectRatio = (width?: number, height?: number): CanonicalAspectRatio | undefined => {
  if (!width || !height || width <= 0 || height <= 0) return undefined
  const target = width / height
  let nearest = CANONICAL_RATIO_VALUES[0]
  let minDiff = Math.abs(target - nearest.value)
  for (let i = 1; i < CANONICAL_RATIO_VALUES.length; i += 1) {
    const diff = Math.abs(target - CANONICAL_RATIO_VALUES[i].value)
    if (diff < minDiff) {
      minDiff = diff
      nearest = CANONICAL_RATIO_VALUES[i]
    }
  }
  return minDiff / target <= SNAP_TOLERANCE ? nearest.label : undefined
}

/**
 * Build the three flat dimension fields for a payload, omitting empty keys.
 *
 * Accepts an already-computed aspectRatio (pass-through) or derives one from
 * width/height. This lets upstream enrichers (useDRMUpload, useGiphy,
 * applyOEmbedMeta) set aspectRatio once and have the payload helper reuse it
 * without recomputing.
 */
export const buildMediaDimensionFields = (dims: MediaDimensionFields): MediaDimensionFields => {
  const { width, height, aspectRatio } = dims
  const resolvedAspectRatio = aspectRatio ?? computeAspectRatio(width, height)
  const fields: MediaDimensionFields = {}
  if (width) fields.width = width
  if (height) fields.height = height
  if (resolvedAspectRatio) fields.aspectRatio = resolvedAspectRatio
  return fields
}

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined'

/**
 * Safety net for measurement: if the browser never fires load/error/metadata
 * (e.g. stalled network, decode hangs, broken source), resolve empty instead
 * of dangling. Kept generous so real media on slow connections still measures.
 */
const MEASURE_TIMEOUT_MS = 5000

const measureImageFromUrl = (url: string): Promise<RawMediaDimensions> =>
  Promise.race<RawMediaDimensions>([
    new Promise<RawMediaDimensions>(resolve => {
      const img = new Image()
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
      img.onerror = () => resolve({})
      img.src = url
    }),
    new Promise<RawMediaDimensions>(resolve => setTimeout(() => resolve({}), MEASURE_TIMEOUT_MS)),
  ])

const measureVideoFromUrl = (url: string): Promise<RawMediaDimensions> =>
  Promise.race<RawMediaDimensions>([
    new Promise<RawMediaDimensions>(resolve => {
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.muted = true
      video.onloadedmetadata = () => resolve({ width: video.videoWidth, height: video.videoHeight })
      video.onerror = () => resolve({})
      video.src = url
    }),
    new Promise<RawMediaDimensions>(resolve => setTimeout(() => resolve({}), MEASURE_TIMEOUT_MS)),
  ])

/**
 * Measure intrinsic dimensions from an already-available URL (blob: or http).
 * Caller specifies `kind` because a generic URL does not imply a content type.
 */
export const getMediaDimensionsFromUrl = async (
  url: string,
  kind: MediaDimensionKind
): Promise<RawMediaDimensions> => {
  if (!isBrowser() || !url) return {}
  if (kind === 'image') return measureImageFromUrl(url)
  if (kind === 'video') return measureVideoFromUrl(url)
  return {}
}

/**
 * Measure intrinsic dimensions from a File object by creating a temporary
 * object URL. The URL is revoked after measurement to avoid leaks.
 */
export const getMediaDimensionsFromFile = async (file: File): Promise<RawMediaDimensions> => {
  if (!isBrowser() || !file) return {}
  const mime = file.type || ''
  const kind: MediaDimensionKind | undefined = mime.startsWith('image/')
    ? 'image'
    : mime.startsWith('video/')
      ? 'video'
      : undefined
  if (!kind) return {}

  // Guard against environments (e.g. jsdom/node test runners) where
  // URL.createObjectURL does not accept File objects. Any failure degrades
  // gracefully to an empty result so upload flows are never blocked.
  let objectUrl: string
  try {
    objectUrl = URL.createObjectURL(file)
  } catch {
    return {}
  }

  try {
    return await getMediaDimensionsFromUrl(objectUrl, kind)
  } finally {
    try {
      URL.revokeObjectURL(objectUrl)
    } catch {
      // ignore revoke errors in constrained environments
    }
  }
}
